import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAttractions } from '@/hooks/use-attractions';

interface Props {
  value: string;
  onChange: (name: string, attractionId?: number | null) => void;
  placeholder?: string;
  className?: string;
}

export function AttractionAutocomplete({ value, onChange, placeholder, className }: Props) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const shouldSearch = query.trim().length >= 3;
  const { data: results = [] } = useAttractions(shouldSearch ? { q: query } : {});
  const open = focused && shouldSearch && results.length > 0;

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setFocused(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value, null);
            setQuery(e.target.value);
          }}
          onFocus={() => {
            setQuery(value);
            setFocused(true);
          }}
          placeholder={placeholder}
          className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-3 font-mono text-[12px] uppercase tabular-nums text-foreground outline-none transition-colors focus:border-foreground"
        />
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
      </div>

      {open && (
        <div className="absolute z-40 mt-1 left-0 right-0 rounded-lg border border-border bg-card shadow-lg overflow-hidden max-h-56 overflow-y-auto">
          {results.slice(0, 8).map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                onChange(a.name, a.id);
                setFocused(false);
              }}
              className="w-full text-left px-3 py-2 mono uppercase text-[12px] tabular-nums tracking-wider hover:bg-muted flex items-center justify-between"
            >
              <span className="text-foreground font-bold">{a.name}</span>
              <span className="text-muted-foreground">salva</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
